import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit, Trash2, Search, Upload, Download, 
  FileText, Eye, EyeOff, Save, X, Check,
  Image, Link as LinkIcon, Globe, Code,
  Layout, Type, Palette, Settings, AlertCircle
} from 'lucide-react';
import { getAllPages, savePageContent, deletePage, Page } from '../../../lib/pageService';
import PageEditor from '../../../components/admin/PageEditor';

const PageManagement = () => {
  const [pages, setPages] = useState<Page[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTemplate, setFilterTemplate] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [editingPage, setEditingPage] = useState<Page | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadPages = async () => {
      try {
        setIsLoading(true);
        const data = await getAllPages();
        setPages(data);
        setError(null);
      } catch (err) {
        setError('Sayfalar yüklenirken bir hata oluştu.');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadPages();
  }, []);

  const filteredPages = pages.filter(page => {
    const matchesSearch = page.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         page.slug.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTemplate = !filterTemplate || page.template === filterTemplate;
    const matchesStatus = !filterStatus || 
                         (filterStatus === 'published' && page.isPublished) ||
                         (filterStatus === 'draft' && !page.isPublished);
    
    return matchesSearch && matchesTemplate && matchesStatus;
  });

  const getTemplateIcon = (template: string) => {
    switch (template) {
      case 'landing': return <Layout size={16} className="text-purple-600" />;
      case 'article': return <FileText size={16} className="text-blue-600" />;
      case 'legal': return <Code size={16} className="text-green-600" />;
      case 'json': return <Code size={16} className="text-orange-600" />;
      default: return <Globe size={16} className="text-slate-600" />;
    }
  };

  const getTemplateText = (template: string) => {
    switch (template) {
      case 'landing': return 'Landing Page';
      case 'article': return 'Makale';
      case 'legal': return 'Yasal Metin';
      case 'json': return 'JSON İçerik';
      default: return 'Varsayılan';
    }
  };

  const handleEdit = (page: Page) => {
    setEditingPage(page);
    setIsModalOpen(true);
  };

  const handleAdd = () => {
    setEditingPage(null);
    setIsModalOpen(true);
  };

  const handleDelete = async (pageId: string) => {
    if (confirm('Bu sayfayı silmek istediğinizden emin misiniz?')) {
      try {
        const success = await deletePage(pageId);
        if (success) {
          setPages(prev => prev.filter(page => page.id !== pageId));
        } else {
          alert('Sayfa silinirken bir hata oluştu.');
        }
      } catch (err) {
        console.error('Error deleting page:', err);
        alert('Sayfa silinirken bir hata oluştu.');
      }
    }
  };

  const togglePageStatus = async (pageId: string) => {
    const page = pages.find(p => p.id === pageId);
    if (!page) return;
    
    const updatedPage = { 
      ...page, 
      isPublished: !page.isPublished,
      publishedAt: !page.isPublished ? new Date().toISOString() : undefined
    };
    
    try {
      const savedPage = await savePageContent(updatedPage);
      if (savedPage) {
        setPages(prev => prev.map(p => p.id === pageId ? savedPage : p));
      }
    } catch (err) {
      console.error('Error updating page status:', err);
      alert('Sayfa durumu güncellenirken bir hata oluştu.');
    }
  };

  const handleSavePage = async () => {
    // Refresh the page list after saving
    try {
      setIsLoading(true);
      const data = await getAllPages();
      setPages(data);
      setIsModalOpen(false);
      setEditingPage(null);
    } catch (err) {
      console.error('Error refreshing pages:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const createHomePage = () => {
    const homePageContent = {
      title: 'Ana Sayfa',
      slug: 'anasayfa',
      content: JSON.stringify({
        heroTitle: 'Bankaları Karşılaştır, En Uygun Olana Başvur',
        heroSubtitle: 'Türkiye\'nin en kapsamlı banka ve kredi karşılaştırma platformu',
        banksTitle: 'Birlikte Çalıştığımız Bankalar',
        banksSubtitle: 'Kredipark.com\'da sadece avantajlı teklifler sunan bankaların ürünleri ve teklifleri yer almaktadır.',
        faqTitle: 'Sık Sorulan Sorular',
        faqSubtitle: 'Kredi başvurusu ve süreçleri hakkında merak ettiğiniz tüm sorular ve cevapları',
        features: [
          {
            title: "En Güncel Oranlar",
            description: "Tüm bankaların güncel faiz oranlarını anlık olarak karşılaştırın"
          },
          {
            title: "Güvenli Başvuru",
            description: "SSL sertifikalı güvenli altyapı ile başvurunuzu hemen yapın"
          },
          {
            title: "Hızlı Sonuç",
            description: "Kredi başvurunuz anında değerlendirilir ve sonuçlandırılır"
          }
        ],
        creditOptions: [
          {
            title: "Kobi Kredisi",
            path: "/kobi-kredisi",
            iconName: "Store"
          },
          {
            title: "İhtiyaç Kredisi",
            path: "/ihtiyac-kredisi",
            iconName: "CreditCard"
          },
          {
            title: "Konut Kredisi",
            path: "/konut-kredisi",
            iconName: "Home"
          },
          {
            title: "Taşıt Kredisi",
            path: "/tasit-kredisi",
            iconName: "Car"
          },
          {
            title: "Mevduat Faizi",
            path: "/mevduat",
            iconName: "Clock"
          },
          {
            title: "Müşteri Ol",
            path: "/uzaktan-musteri-edinimi",
            iconName: "UserRound"
          },
          {
            title: "Kredi Kartları",
            path: "/kredi-kartlari",
            iconName: "CreditCard"
          },
          {
            title: "Ticari Kartlar",
            path: "/kredi-kartlari?type=ticari",
            iconName: "Building2"
          },
          {
            title: "Faizsiz Kredi",
            path: "/ihtiyac-kredisi",
            iconName: "BanknoteIcon"
          }
        ],
        bankLogos: [
          {
            id: "akbank",
            name: "Akbank",
            logo: "https://cdn.hangikredi.com/images/bankdigitalonboarding/a80c0972-6830-4dc4-b3bc-e236c7fbb5ea.png"
          },
          {
            id: "garanti",
            name: "Garanti BBVA",
            logo: "https://cdn.hangikredi.com/images/bankdigitalonboarding/2c60a02b-d37c-48ce-ad8e-deb4e57bf810.jpg"
          },
          {
            id: "yapikredi",
            name: "Yapı Kredi",
            logo: "https://cdn.hangikredi.com/images/bankdigitalonboarding/52f6626d-498d-4e76-8fb5-fd4381a1e0db.png"
          },
          {
            id: "qnb",
            name: "QNB Finansbank",
            logo: "https://cdn.hangikredi.com/images/bankdigitalonboarding/d9ae3539-f63d-4295-84e6-08dbe6e2a6d6.png"
          },
          {
            id: "isbankasi",
            name: "İş Bankası",
            logo: "https://cdn.hangikredi.com/images/bankdigitalonboarding/3b159da8-fe94-4b12-b9fa-4b9c60495305.png"
          },
          {
            id: "ziraatbankasi",
            name: "Ziraat Bankası",
            logo: "https://cdn.hangikredi.com/images/bankdigitalonboarding/2cf1c9fe-fbff-4874-a365-fd63455d078c.png"
          },
          {
            id: "nkolay",
            name: "Nkolay",
            logo: "https://cdn.hangikredi.com/images/bankdigitalonboarding/7accd65f-06ae-48b1-875e-6d9827590be6.png"
          }
        ],
        faqs: [
          {
            question: "Kredi başvurusu için hangi belgeler gereklidir?",
            answer: "Kredi başvurusu için temel olarak şu belgeler gereklidir:\n\n• Kimlik belgesi (Nüfus cüzdanı, ehliyet veya pasaport)\n• Gelir belgesi (Maaş bordrosu veya serbest çalışanlar için gelir beyanı)\n• İkametgah belgesi\n• Son 3 aylık banka hesap özeti\n\nBazı bankalar ek belgeler talep edebilir. Başvuru öncesi seçtiğiniz bankanın gereksinimlerini kontrol etmenizi öneririz.",
            category: "Kredi Başvurusu"
          },
          {
            question: "Online kredi başvurusu ne kadar sürede sonuçlanır?",
            answer: "Online kredi başvuruları genellikle çok hızlı değerlendirilir:\n\n• İhtiyaç kredisi: 5-10 dakika\n• Konut kredisi: 24-48 saat\n• Taşıt kredisi: 1-2 saat\n\nBaşvurunuzun hızlı sonuçlanması için tüm belgelerin eksiksiz ve doğru olması önemlidir.",
            category: "Kredi Başvurusu"
          },
          {
            question: "Kredi faiz oranları nasıl belirlenir?",
            answer: "Kredi faiz oranları şu faktörlere göre belirlenir:\n\n• TCMB politika faizi\n• Kredi türü (ihtiyaç, konut, taşıt)\n• Vade süresi\n• Kredi tutarı\n• Müşteri kredi notu\n• Bankanın risk politikaları\n\nKrediPark üzerinden tüm bankaların güncel faiz oranlarını karşılaştırabilirsiniz.",
            category: "Kredi Hesaplama"
          },
          {
            question: "Kredi taksit tutarı nasıl hesaplanır?",
            answer: "Kredi taksit tutarı şu bileşenleri içerir:\n\n• Ana para\n• Faiz tutarı\n• KKDF (Kaynak Kullanımı Destekleme Fonu)\n• BSMV (Banka Sigorta Muamele Vergisi)\n\nKrediPark'ın kredi hesaplama aracı ile farklı vade ve tutarlara göre ödeme planınızı kolayca oluşturabilirsiniz.",
            category: "Kredi Hesaplama"
          },
          {
            question: "Kredi notum düşükse kredi çekebilir miyim?",
            answer: "Düşük kredi notuna sahip kişiler için çeşitli alternatifler mevcuttur:\n\n• Kefil göstererek kredi başvurusu\n• İpotek karşılığı kredi\n• Düşük kredi notlular için özel kredi paketleri\n• Teminatlı kredi seçenekleri\n\nKrediPark, kredi notunuza uygun en avantajlı seçenekleri sunmaktadır.",
            category: "Kredi Notu"
          },
          {
            question: "Kredi notumu nasıl yükseltebilirim?",
            answer: "Kredi notunuzu yükseltmek için uygulayabileceğiniz stratejiler:\n\n• Kredi kartı ve kredi ödemelerinizi zamanında yapın\n• Kredi kartı limitinizin %30'undan fazlasını kullanmayın\n• Gereksiz kredi başvurularından kaçının\n• Düzenli gelir akışınızı koruyun\n• Çeşitli kredi ürünlerini kontrollü kullanın",
            category: "Kredi Notu"
          },
          {
            question: "Kredi erken kapatma durumunda ceza ödenir mi?",
            answer: "Kredi erken kapatma işlemlerinde:\n\n• Tüketici kredilerinde yasal sınırlamalar mevcuttur\n• Kalan vadeye göre hesaplanan makul bir erken ödeme ücreti alınabilir\n• Erken kapatma ücreti maksimum oranları yasa ile belirlenmiştir\n• Konut kredilerinde erken ödeme cezası daha düşüktür\n\nErken ödeme yapmadan önce bankanızdan detaylı bilgi almanızı öneririz.",
            category: "Ödemeler ve Vadeler"
          },
          {
            question: "Taksit ödemelerimi nasıl yapabilirim?",
            answer: "Kredi taksit ödemelerinizi şu kanallardan yapabilirsiniz:\n\n• İnternet bankacılığı\n• Mobil bankacılık\n• ATM'ler\n• Banka şubeleri\n• Otomatik ödeme talimatı\n\nÖdeme tarihinden önce hesabınızda yeterli bakiye bulundurmanız önemlidir.",
            category: "Ödemeler ve Vadeler"
          },
          {
            question: "Emekliler hangi kredi avantajlarından yararlanabilir?",
            answer: "Emekliler için özel kredi avantajları:\n\n• Düşük faiz oranları\n• Esnek ödeme seçenekleri\n• Masrafsız kredi imkanları\n• Yüksek kredi limitleri\n• Özel sigorta paketleri\n\nKrediPark üzerinden emeklilere özel kampanyaları inceleyebilirsiniz.",
            category: "Özel Durumlar"
          },
          {
            question: "Yapılandırma veya refinansman kredisi nedir?",
            answer: "Yapılandırma kredisi mevcut kredilerinizi tek bir kredide toplama imkanı sunar:\n\n• Daha düşük faiz oranı\n• Tek ve düzenli ödeme planı\n• Uzun vade seçeneği\n• Aylık taksit tutarında azalma\n• Borç yönetiminde kolaylık\n\nMevcut kredi borçlarınızı yapılandırmak için bankaların özel tekliflerini karşılaştırabilirsiniz.",
            category: "Özel Durumlar"
          }
        ],
        newsletter: {
          title: "Fırsatlardan Haberdar Olun",
          subtitle: "En güncel banka kampanyaları, uygun kredi fırsatları ve size özel tekliflerden ilk siz haberdar olun.",
          buttonText: "Fırsatlardan Haberdar Ol",
          successTitle: "Teşekkürler!",
          successMessage: "Başvurunuz başarıyla alındı. En güncel kampanya ve fırsatlardan haberdar olmak için e-posta ve SMS bildirimlerinizi takip edebilirsiniz.",
          emailPlaceholder: "E-posta Adresi",
          phonePlaceholder: "Telefon Numarası",
          kvkkText: "Kişisel verilerimin işlenmesine ilişkin Aydınlatma Metni'ni okudum ve kişisel verilerimin bu kapsamda işlenmesini kabul ediyorum.",
          marketingText: "KrediPark'a özel kampanyalar, avantajlı teklifler ve güncel fırsatlardan e-posta ve SMS yoluyla haberdar olmak istiyorum."
        },
        components: [
          {
            id: "component-1",
            type: "faq",
            title: "Sık Sorulan Sorular",
            order: 1,
            settings: {
              title: "Sık Sorulan Sorular",
              subtitle: "Merak ettiğiniz tüm sorular ve cevapları",
              maxItems: 10,
              showCategories: true
            },
            content: {
              faqs: [
                {
                  question: "Kredi başvurusu için hangi belgeler gereklidir?",
                  answer: "Kredi başvurusu için temel olarak şu belgeler gereklidir:\n\n• Kimlik belgesi (Nüfus cüzdanı, ehliyet veya pasaport)\n• Gelir belgesi (Maaş bordrosu veya serbest çalışanlar için gelir beyanı)\n• İkametgah belgesi\n• Son 3 aylık banka hesap özeti\n\nBazı bankalar ek belgeler talep edebilir. Başvuru öncesi seçtiğiniz bankanın gereksinimlerini kontrol etmenizi öneririz.",
                  category: "Kredi Başvurusu"
                },
                {
                  question: "Online kredi başvurusu ne kadar sürede sonuçlanır?",
                  answer: "Online kredi başvuruları genellikle çok hızlı değerlendirilir:\n\n• İhtiyaç kredisi: 5-10 dakika\n• Konut kredisi: 24-48 saat\n• Taşıt kredisi: 1-2 saat\n\nBaşvurunuzun hızlı sonuçlanması için tüm belgelerin eksiksiz ve doğru olması önemlidir.",
                  category: "Kredi Başvurusu"
                },
                {
                  question: "Kredi faiz oranları nasıl belirlenir?",
                  answer: "Kredi faiz oranları şu faktörlere göre belirlenir:\n\n• TCMB politika faizi\n• Kredi türü (ihtiyaç, konut, taşıt)\n• Vade süresi\n• Kredi tutarı\n• Müşteri kredi notu\n• Bankanın risk politikaları\n\nKrediPark üzerinden tüm bankaların güncel faiz oranlarını karşılaştırabilirsiniz.",
                  category: "Kredi Hesaplama"
                }
              ]
            }
          },
          {
            id: "component-2",
            type: "bankList",
            title: "Banka Listesi",
            order: 2,
            settings: {
              title: "Birlikte Çalıştığımız Bankalar",
              subtitle: "Kredipark.com'da sadece avantajlı teklifler sunan bankaların ürünleri ve teklifleri yer almaktadır.",
              autoScroll: true,
              showNames: true
            },
            content: {
              bankLogos: [
                {
                  id: "akbank",
                  name: "Akbank",
                  logo: "https://cdn.hangikredi.com/images/bankdigitalonboarding/a80c0972-6830-4dc4-b3bc-e236c7fbb5ea.png"
                },
                {
                  id: "garanti",
                  name: "Garanti BBVA",
                  logo: "https://cdn.hangikredi.com/images/bankdigitalonboarding/2c60a02b-d37c-48ce-ad8e-deb4e57bf810.jpg"
                },
                {
                  id: "yapikredi",
                  name: "Yapı Kredi",
                  logo: "https://cdn.hangikredi.com/images/bankdigitalonboarding/52f6626d-498d-4e76-8fb5-fd4381a1e0db.png"
                },
                {
                  id: "qnb",
                  name: "QNB Finansbank",
                  logo: "https://cdn.hangikredi.com/images/bankdigitalonboarding/d9ae3539-f63d-4295-84e6-08dbe6e2a6d6.png"
                },
                {
                  id: "isbankasi",
                  name: "İş Bankası",
                  logo: "https://cdn.hangikredi.com/images/bankdigitalonboarding/3b159da8-fe94-4b12-b9fa-4b9c60495305.png"
                }
              ]
            }
          },
          {
            id: "component-3",
            type: "newsletter",
            title: "Bülten Kaydı",
            order: 3,
            settings: {
              title: "Fırsatlardan Haberdar Olun",
              subtitle: "En güncel banka kampanyaları, uygun kredi fırsatları ve size özel tekliflerden ilk siz haberdar olun.",
              buttonText: "Fırsatlardan Haberdar Ol",
              successTitle: "Teşekkürler!",
              successMessage: "Başvurunuz başarıyla alındı. En güncel kampanya ve fırsatlardan haberdar olmak için e-posta ve SMS bildirimlerinizi takip edebilirsiniz.",
              emailPlaceholder: "E-posta Adresi",
              phonePlaceholder: "Telefon Numarası",
              kvkkText: "Kişisel verilerimin işlenmesine ilişkin Aydınlatma Metni'ni okudum ve kişisel verilerimin bu kapsamda işlenmesini kabul ediyorum.",
              marketingText: "KrediPark'a özel kampanyalar, avantajlı teklifler ve güncel fırsatlardan e-posta ve SMS yoluyla haberdar olmak istiyorum.",
              showPhone: true,
              showEmail: true
            }
          }
        ]
      }, null, 2),
      metaTitle: 'KrediPark - Kredi ve Banka Karşılaştırma',
      metaDescription: 'Türkiye\'nin en kapsamlı kredi ve banka karşılaştırma platformu. En uygun kredi ve kredi kartı tekliflerini karşılaştırın, size en uygun olanı seçin.',
      isPublished: true,
      isFeatured: true,
      template: 'json',
      author: 'Admin'
    };

    setEditingPage(null);
    setIsModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="flex items-center gap-4">
            <div className="w-8 h-8 border-4 border-[#F7BA45] border-t-transparent rounded-full animate-spin"></div>
            <p className="text-slate-700">Sayfalar yükleniyor...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="flex items-center gap-4 text-red-600">
            <AlertCircle size={24} />
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Sayfa İçerikleri</h1>
              <p className="text-slate-600">Web sitesi sayfalarını oluşturun ve yönetin</p>
            </div>
            <div className="flex items-center gap-3">
              <button 
                onClick={createHomePage}
                className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
              >
                <Layout size={20} />
                <span>Ana Sayfa Oluştur</span>
              </button>
              <button 
                onClick={handleAdd}
                className="flex items-center gap-2 px-4 py-2 bg-[#F7BA45] text-slate-900 rounded-lg hover:bg-[#e5ac3d] transition-colors"
              >
                <Plus size={20} />
                <span>Yeni Sayfa</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Search and Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
              <input
                type="text"
                placeholder="Sayfa ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
              />
            </div>
            <select
              value={filterTemplate}
              onChange={(e) => setFilterTemplate(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            >
              <option value="">Tüm Şablonlar</option>
              <option value="default">Varsayılan</option>
              <option value="landing">Landing Page</option>
              <option value="article">Makale</option>
              <option value="legal">Yasal Metin</option>
              <option value="json">JSON İçerik</option>
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            >
              <option value="">Tüm Durumlar</option>
              <option value="published">Yayınlanan</option>
              <option value="draft">Taslak</option>
            </select>
          </div>
        </div>

        {/* Pages Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Sayfa
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Şablon
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Durum
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Yazar
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Son Güncelleme
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {filteredPages.map((page) => (
                  <tr key={page.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-slate-900">{page.title}</div>
                        <div className="text-sm text-slate-500">/{page.slug}</div>
                        <div className="text-xs text-slate-400 mt-1 line-clamp-1">{page.metaDescription}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {getTemplateIcon(page.template)}
                        <span className="text-sm text-slate-900">{getTemplateText(page.template)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        page.isPublished 
                          ? 'text-green-700 bg-green-100' 
                          : 'text-yellow-700 bg-yellow-100'
                      }`}>
                        {page.isPublished ? <Eye size={12} /> : <EyeOff size={12} />}
                        {page.isPublished ? 'Yayınlanan' : 'Taslak'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-slate-900">{page.author}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-slate-900">
                        {new Date(page.updatedAt).toLocaleDateString('tr-TR')}
                      </div>
                      <div className="text-xs text-slate-500">
                        {new Date(page.updatedAt).toLocaleTimeString('tr-TR')}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => togglePageStatus(page.id)}
                          className={`p-1 rounded-lg ${
                            page.isPublished 
                              ? 'text-green-600 hover:bg-green-100' 
                              : 'text-yellow-600 hover:bg-yellow-100'
                          }`}
                        >
                          {page.isPublished ? <Eye size={16} /> : <EyeOff size={16} />}
                        </button>
                        <button
                          onClick={() => handleEdit(page)}
                          className="p-1 text-slate-400 hover:text-slate-600"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(page.id)}
                          className="p-1 text-slate-400 hover:text-red-600"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Page Editor Modal */}
      {isModalOpen && (
        <PageEditor 
          page={editingPage}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSavePage}
        />
      )}
    </div>
  );
};

export default PageManagement;